"use client"

import { gsap } from "gsap"
import { PointerEvent, useEffect, useRef } from "react"

const MAGNET_STRENGTH = 0.22
const MAGNET_MAX = 7

function clampOffset(value: number, max: number) {
  return Math.max(-max, Math.min(max, value))
}

/** Same cursor pull used by the gallery subcategory filters. */
export function useMagneticPull<T extends HTMLElement>() {
  const ref = useRef<T>(null)
  const xTo = useRef<((value: number) => gsap.core.Tween) | null>(null)
  const yTo = useRef<((value: number) => gsap.core.Tween) | null>(null)
  const reducedMotionRef = useRef(false)

  useEffect(() => {
    const element = ref.current

    if (!element) {
      return
    }

    const motionQuery = window.matchMedia("(prefers-reduced-motion: reduce)")
    const syncMotionPreference = () => {
      reducedMotionRef.current = motionQuery.matches
    }

    syncMotionPreference()
    motionQuery.addEventListener("change", syncMotionPreference)

    xTo.current = gsap.quickTo(element, "x", {
      duration: 0.55,
      ease: "power3.out",
    })
    yTo.current = gsap.quickTo(element, "y", {
      duration: 0.55,
      ease: "power3.out",
    })

    return () => {
      motionQuery.removeEventListener("change", syncMotionPreference)
      xTo.current = null
      yTo.current = null
      gsap.killTweensOf(element)
      gsap.set(element, { x: 0, y: 0 })
    }
  }, [])

  const onPointerMove = (event: PointerEvent<T>) => {
    // Effetto elastico solo su desktop: mai su mobile/tablet stretto.
    if (
      event.pointerType !== "mouse" ||
      reducedMotionRef.current ||
      !window.matchMedia("(min-width: 801px)").matches
    ) {
      return
    }

    const element = ref.current

    if (!element) {
      return
    }

    const rect = element.getBoundingClientRect()
    const offsetX = event.clientX - (rect.left + rect.width / 2)
    const offsetY = event.clientY - (rect.top + rect.height / 2)

    xTo.current?.(clampOffset(offsetX * MAGNET_STRENGTH, MAGNET_MAX))
    yTo.current?.(clampOffset(offsetY * MAGNET_STRENGTH, MAGNET_MAX))
  }

  const onPointerLeave = () => {
    xTo.current?.(0)
    yTo.current?.(0)
  }

  return { ref, onPointerMove, onPointerLeave }
}
